/**
 * Owns the waitlist modal so any button on any page can open it.
 *
 * Without this, every page would need its own `open` state and its own copy of
 * the modal, and the nav bar — which is rendered outside the page — could not
 * open one at all. One provider at the root, one modal in the DOM.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { WaitlistModal } from './WaitlistModal';

interface WaitlistValue {
  /** `source` is recorded in the notification email, so we can see which CTA works. */
  open: (source: string) => void;
  close: () => void;
}

const WaitlistContext = createContext<WaitlistValue | null>(null);

export function WaitlistProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState('unknown');

  const open = useCallback((nextSource: string) => {
    setSource(nextSource);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<WaitlistValue>(() => ({ open, close }), [open, close]);

  return (
    <WaitlistContext.Provider value={value}>
      {children}
      <WaitlistModal open={isOpen} onClose={close} source={source} />
    </WaitlistContext.Provider>
  );
}

/** Call `open('hero')` from any button that would otherwise link to a store. */
export function useWaitlist(): WaitlistValue {
  const value = useContext(WaitlistContext);
  if (!value) throw new Error('useWaitlist must be used inside <WaitlistProvider>.');
  return value;
}
